import { useState } from "react";
import { Download, RotateCcw, FileDown, FileUp, AlertCircle, AlertTriangle, X, Loader2 } from "lucide-react";
import { useConfirm } from "@omit/react-confirm-dialog";
import { Button } from "@/modern/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/modern/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import { FileUpload, FileUploadDropzone, FileUploadTrigger } from "@/modern/components/ui/file-upload";
import { useSettingsContext } from "../context/SettingsContext";
import { SettingsData } from "../constants/settingsStructure";
import { ValidationError } from "../hooks/useSettings";
import SettingsView from "../SettingsView";

const DataManagementView = () => {
  const [previewData, setPreviewData] = useState<SettingsData | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [processingFileName, setProcessingFileName] = useState<string | null>(null);
  const [uploadKey, setUploadKey] = useState(0); // Key to force FileUpload component remount

  const confirm = useConfirm();
  const { csvData, saveSettings, getTemplateFile, templateData, exportSettingsToCSV, importSettingsFromCSV, setIsDataManagementOpen } = useSettingsContext();

  const handleExportCSV = () => {
    try {
      exportSettingsToCSV(csvData);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  const handleResetToDefault = () => {
    // Set preview data to template data
    setPreviewData(templateData);
    setShowPreview(true);
  };

  const handleFileImport = async (files: File[]) => {
    if (files.length === 0) {
      // Clear states when no files
      setImportError(null);
      setValidationErrors([]);
      setProcessingFileName(null);
      return;
    }

    const file = files[0];
    setIsProcessing(true);
    setImportError(null);
    setValidationErrors([]);
    setProcessingFileName(file.name);

    try {
      // Import and validate CSV using hook
      const result = await importSettingsFromCSV(file);

      // Set validation errors (warnings only, since errors would have thrown)
      setValidationErrors(result.errors);

      // Set preview data and show preview immediately
      setPreviewData(result.data);
      setShowPreview(true);
      setProcessingFileName(null);

      // Reset the FileUpload component by incrementing key
      setUploadKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error importing CSV:", error);
      setImportError(error instanceof Error ? error.message : "Failed to import CSV file");
      setProcessingFileName(null);

      // Reset the FileUpload component by incrementing key
      setUploadKey((prev) => prev + 1);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSavePreview = async () => {
    if (previewData) {
      const confirmed = await confirm({
        title: "Confirm Save Changes",
        description: "Are you sure you want to save these settings? This will update your current configuration.",
        customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
          <div className="space-x-4">
            <Button variant="ghost" onClick={cancel}>
              Cancel
            </Button>
            <Button variant="default" onClick={confirm}>
              Save Changes
            </Button>
          </div>
        )
      });

      if (confirmed) {
        try {
          setIsProcessing(true);
          await saveSettings(previewData);
          setShowPreview(false);
          setPreviewData(null);
          setValidationErrors([]);
          setImportError(null);
          setIsDataManagementOpen(false);
        } catch (error) {
          console.error("Error saving settings:", error);
          // Error is already handled by saveSettings with toast
        } finally {
          setIsProcessing(false);
        }
      }
    }
  };

  const handleCancelPreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setValidationErrors([]);
    setImportError(null);
    // Keep uploadKey as is - it was already reset after successful import
  };

  const clearImportError = () => {
    setImportError(null);
    setValidationErrors([]);
    // uploadKey was already reset when error occurred
  };

  return (
    <>
      <div className="p-6">
        {/* Grid Layout for better organization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Export Section - Subtle Blue Accent */}
          <Card className="border-border/60 bg-card hover:border-border transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted/60">
                  <FileDown className="h-5 w-5 text-blue-600/70 dark:text-blue-400/70" />
                </div>
                <div>
                  <CardTitle className="text-base">Export Settings</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Download your data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button onClick={handleExportCSV} className="w-full" size="sm">
                <Download className="h-4 w-4" />
                Export Current Settings
              </Button>
              <Button onClick={getTemplateFile} className="w-full" variant="outline" size="sm">
                <Download className="h-4 w-4" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          {/* Reset Section - Subtle Amber Accent */}
          <Card className="border-border/60 bg-card hover:border-border transition-colors">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted/60">
                  <RotateCcw className="h-5 w-5 text-orange-600/70 dark:text-orange-400/70" />
                </div>
                <div>
                  <CardTitle className="text-base">Reset to Default</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Restore factory settings</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button onClick={handleResetToDefault} className="w-full" variant="outline" size="sm">
                <RotateCcw className="h-4 w-4" />
                Reset to Factory Defaults
              </Button>
              <div className="flex items-center gap-2 mt-3 p-2.5 rounded-md bg-muted/40 border border-border/50">
                <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">This will preview default values before applying</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Import Section - Subtle Purple Accent - Full Width */}
        <Card className="border-border/60 bg-card hover:border-border transition-colors">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-muted/60">
                <FileUp className="h-5 w-5 text-purple-600/70 dark:text-purple-400/70" />
              </div>
              <div>
                <CardTitle className="text-base">Import Settings</CardTitle>
                <CardDescription className="text-xs mt-0.5">Upload a CSV file to preview and apply new settings</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <FileUpload
              key={uploadKey}
              accept=".csv"
              maxFiles={1}
              maxSize={5 * 1024 * 1024} // 5MB
              onValueChange={handleFileImport}
              disabled={isProcessing}
            >
              <FileUploadDropzone className="min-h-[160px] hover:bg-muted/30">
                <div className="flex flex-col items-center gap-3 text-center">
                  <div className={`p-3 rounded-full bg-muted/60 ${isProcessing ? "animate-pulse" : ""}`}>
                    <FileUp className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{isProcessing ? "Validating CSV..." : "Drop your CSV file here"}</p>
                    {isProcessing && processingFileName && <p className="text-xs text-muted-foreground truncate max-w-[300px]">{processingFileName}</p>}
                    {!isProcessing && <p className="text-xs text-muted-foreground">or click to browse</p>}
                  </div>
                  <FileUploadTrigger asChild>
                    <Button variant="outline" size="sm" disabled={isProcessing}>
                      <FileUp className="h-4 w-4" />
                      Choose File
                    </Button>
                  </FileUploadTrigger>
                  <p className="text-xs text-muted-foreground">CSV files only • Maximum 5MB</p>
                </div>
              </FileUploadDropzone>
            </FileUpload>

            {/* Import Error Display */}
            {importError && (
              <div className="mt-4 p-4 rounded-md bg-destructive/10 border border-destructive/30 animate-in fade-in-0 slide-in-from-top-2">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm text-destructive mb-2">Import Failed</h4>
                    <div className="text-sm text-destructive/90 whitespace-pre-wrap">{importError}</div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-destructive/20" onClick={clearImportError}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview && !!previewData} onOpenChange={(open) => !open && handleCancelPreview()}>
        <DialogContent showCloseButton={false} className="sm:max-w-[80vw] h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          {/* Loading Overlay */}
          {isProcessing && (
            <div className="absolute inset-0 bg-background/80 z-50 flex items-center justify-center">
              <div className="p-6 flex flex-col items-center gap-3">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
                <p className="text-sm font-medium text-foreground">Saving settings...</p>
              </div>
            </div>
          )}

          <DialogHeader className="p-4 border-b">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <DialogTitle>Preview Settings</DialogTitle>
                <DialogDescription>This preview shows a read-only version of the settings you are about to save.</DialogDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleCancelPreview} disabled={isProcessing}>
                  Cancel
                </Button>
                <Button onClick={handleSavePreview} variant="default" disabled={isProcessing}>
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          </DialogHeader>

          {/* Validation Warnings */}
          {validationErrors.length > 0 && (
            <div className="px-4 py-3 border-b bg-muted/20">
              <div className="flex items-start gap-3">
                {validationErrors.some((e) => e.type === "error") ? (
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm mb-2">Import Completed with Warnings</h4>
                  <div className="space-y-2">
                    {validationErrors.map((error, index) => (
                      <div key={index} className="text-sm">
                        <p className="font-medium text-muted-foreground">{error.message}</p>
                        {error.details && error.details.length > 0 && (
                          <ul className="mt-1 ml-4 list-disc text-xs text-muted-foreground space-y-0.5">
                            {error.details.map((detail, detailIndex) => (
                              <li key={detailIndex}>{detail}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setValidationErrors([])}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            {previewData && <SettingsView previewData={previewData} isPreviewMode={true} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DataManagementView;
